/**
 * SettingsModalContext
 * Controls the Settings Modal visibility and initial page
 */

import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

const SettingsModalContext = createContext(null);

/**
 * Provider for settings modal state
 */
export const SettingsModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [initialPage, setInitialPage] = useState('settings');

  /**
   * Open settings modal, optionally to a specific page
   * @param {string} [page='settings'] - The page to open to
   */
  const openSettings = useCallback((page = 'settings') => {
    setInitialPage(page);
    setIsOpen(true);
  }, []);

  /**
   * Close settings modal
   */
  const closeSettings = useCallback(() => {
    setIsOpen(false);
    // Reset to default page after close
    setInitialPage('settings');
  }, []);

  const value = useMemo(() => ({
    isOpen,
    initialPage,
    openSettings,
    closeSettings,
  }), [isOpen, initialPage, openSettings, closeSettings]);

  return (
    <SettingsModalContext.Provider value={value}>
      {children}
    </SettingsModalContext.Provider>
  );
};

SettingsModalProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access settings modal state and actions
 */
export const useSettingsModal = () => {
  const context = useContext(SettingsModalContext);
  if (!context) {
    throw new Error('useSettingsModal must be used within a SettingsModalProvider');
  }
  return context;
};

export default SettingsModalContext;
